<template>
  <ExperienceCollectionTemplate
    map-source-page="destination_theme_lp"
    :all-data-initialized="allDataInitialized"
    :article-section-experiences="articleSectionExperiences"
    :basic-title="`${themeName} in ${destinationName}`"
    :cms-content="destinationThemeContent"
    :destination="destination"
    :destination-tags-query="destinationTagsQuery"
    :experience-count="experienceCount"
    :experience-event-start-at-dates-query="experienceEventStartAtDatesQuery"
    :experience-sections="experienceSections"
    :experiences="experiences"
    :experiences-per-page="EXPERIENCES_PER_PAGE"
    :grid-title="`${themeName} experiences in ${destinationName}`"
    :handle-experience-click="handleExperienceClick"
    :handle-featured-click="handleFeaturedClick"
    :handle-search-click="handleSearchClick"
    :is-loading-experiences="isLoadingExperiences"
    :is-sp="isSP"
    :itinerary-list="itineraryList"
    :on-click-contact="onClickContact"
    :on-click-partner="onClickPartner"
    :on-instant-booking-click="handleInstantBookingClick"
    :on-show-more-click="handleShowMoreClick"
    :on-sort-change="handleSortChange"
    :preference-tags-query="preferenceTagsQuery"
    :selected-sort-type="selectedSortType"
    :theme="theme"
    :theme-tags-query="themeTagsQuery"
  />
</template>

<script setup lang="ts">
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useBuildUrl, useErrorCreator } from '@/wabunka-src/common/composables';
import { useLastModifiedHeader } from '@/wabunka-src/common/composables/general/useLastModifiedHeader/useLastModifiedHeader';
import ExperienceCollectionTemplate from '@/wabunka-src/common/features/Experience/components/ExperienceCollectionTemplate/ExperienceCollectionTemplate.vue';
import { useExperienceCollection } from '@/wabunka-src/common/features/Experience/composables/useExperienceCollection/useExperienceCollection';
import { useArticleSectionExperiences } from '@/wabunka-src/common/features/Featured/composables/useArticleSectionExperiences';
import { useExtractWebSectionIds } from '@/wabunka-src/common/features/Featured/composables/useExtractWebSectionIds';
import { isThemeTag } from '@/wabunka-src/common/models/themeTag';
import { togglePreference } from '@/wabunka-src/common/utils/togglePreference';
import { EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS } from '@/wabunka-src/constants/experienceSectionWebPagePath';
import { ICON_FONT_URL } from '@/wabunka-src/constants/iconFontUrl';
import { useDestinationThemeContentQuery } from '@/wabunka-src/features/DestinationTheme/composables/query/useDestinationThemeContentQuery';
import { useGetExperienceSectionsQuery } from '@/wabunka-src/features/Experience';

const route = useRoute();
const { createWabunkaError } = useErrorCreator();

// 定数定義
const EXPERIENCES_PER_PAGE = 18;

// ルートパラメータからdestinationとcategoryを取得
const destination = route.params.destination as string;
const theme = route.params.theme as string;

// タグデータを取得して動的にバリデーション
const { tagDictByWebPath } = await useAsyncWabunkaTags();

// 有効なdestinationとthemeのwebPathを取得
const validDestinations = computed(() => {
  return Object.keys(tagDictByWebPath.value)
    .filter((path) => tagDictByWebPath.value[path].tagGroupType === 'destination')
    .map((path) => path.replace('/', ''));
});

const validThemes = computed(() => {
  return Object.keys(tagDictByWebPath.value).filter((path) => isThemeTag(tagDictByWebPath.value[path]));
});

// 現在のURLが有効な組み合わせかチェック
const isValidDestination = computed(() => validDestinations.value.includes(destination));
const isValidTheme = computed(() => validThemes.value.includes(theme));

// 有効でない組み合わせの場合は404エラー
if (!isValidDestination.value || !isValidTheme.value) {
  throw createWabunkaError({
    statusCode: 404,
    statusMessage: `Invalid destination or theme combination: ${destination}/${theme}`,
    fatal: true,
  });
}

const [destinationThemeResult, experienceCollectionResult] = await Promise.all([
  useDestinationThemeContentQuery({
    destination,
    theme,
    server: true,
  }),
  useExperienceCollection({
    destination,
    theme,
    experiencesPerPage: EXPERIENCES_PER_PAGE,
  }),
]);

const { destinationThemeContent } = destinationThemeResult;

useLastModifiedHeader(destinationThemeContent.value.revisedAt);

// CMSコンテンツ（ArticleSection）で使用する体験をフィルターなしで取得
// contentsBottom にも ArticleSection が含まれるため、両方を結合して渡す
const { experiences: articleSectionExperiences } = await useArticleSectionExperiences(
  computed(() => [...(destinationThemeContent.value.contents ?? []), ...(destinationThemeContent.value.contentsBottom ?? [])]),
);

// CMS の experienceSectionIDs セクションで表示する体験セクションを取得
const { webSectionIds } = useExtractWebSectionIds(destinationThemeContent);
const { experienceSections } = await useGetExperienceSectionsQuery(EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS.AREA_DETAIL, webSectionIds.value);

const {
  isSP,
  itineraryList,
  experiences,
  meta,
  isLoadingExperiences,
  experienceCount,
  hasNoResponse,
  allDataInitialized,
  destinationTagsQuery,
  themeTagsQuery,
  experienceEventStartAtDatesQuery,
  handleExperienceClick,
  onClickContact,
  onClickPartner,
  handleFeaturedClick,
  handleSearchClick,
  selectedSortType,
  handleSortChange,
  withCurrentSortQuery,
  buildUrlWithEndSlash,
} = experienceCollectionResult;

// Other系タグの定義
const OTHER_THEMES = ['other-culture', 'other-crafts', 'other-art', 'other-cuisine'];
const destinationName = computed(() => {
  const tag = tagDictByWebPath.value[destination];
  return tag.name;
});

const themeName = computed(() => {
  const tag = tagDictByWebPath.value[theme];

  if (tag?.webPath && OTHER_THEMES.includes(tag.webPath)) {
    return tag.tagGroupName;
  }
  return tag.name;
});

// 体験が0件の場合はnoindexにする
// hasNoResponse ガード: 検索データ未取得（エッジキャッシュ通貨不一致等によるハイドレーション時の
// 一時的 null）を「0件」と誤認して noindex を出さない（JCAT-5203）
const shouldIndex = computed(() => hasNoResponse.value || experienceCount.value > 0);

// Show Moreボタンクリック時のハンドラ - 検索ページへ遷移（page=2）
const handleShowMoreClick = () => {
  navigateTo({ path: `/s/experiences/${destination}/${theme}/`, query: withCurrentSortQuery({ page: '2' }) });
};

const INSTANT_BOOKING_WEB_PATH = 'instant-booking';

const preferenceTagsQuery = computed<string>(() => (route.query?.preferences as string) ?? '');

const handleInstantBookingClick = () => {
  const newPreferences = togglePreference(preferenceTagsQuery.value, INSTANT_BOOKING_WEB_PATH);
  const query: Record<string, string> = { page: '1' };
  if (newPreferences) {
    query.preferences = newPreferences;
  }
  navigateTo({ path: `/s/experiences/${destination}/${theme}/`, query: withCurrentSortQuery(query) });
};

// メタ情報の設定
const pageTitle = computed(() => meta.value?.pageTitle || ``);
const pageDescription = computed(() => meta.value?.metaDescription || ``);
const { buildUrl } = useBuildUrl();
const ogImage = computed(() => destinationThemeContent.value.ogImage?.url || buildUrl('/en_ogp.png'));

useSeoMeta({
  title: () => pageTitle.value,
  description: () => pageDescription.value,
  ogTitle: () => pageTitle.value,
  ogDescription: () => pageDescription.value,
  ogImage: () => ogImage.value,
  ogUrl: () => buildUrlWithEndSlash(route.path),
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterTitle: () => pageTitle.value,
  twitterDescription: () => pageDescription.value,
  twitterImage: () => ogImage.value,
});

if (!shouldIndex.value) useRobotsRule('noindex,nofollow');

useHead({
  link: [
    { rel: 'canonical', href: buildUrlWithEndSlash(route.path) },
    { rel: 'stylesheet', href: ICON_FONT_URL },
  ],
});
</script>

<style scoped>
:deep(.is-filtered) {
  border-color: #826b3d !important;
  color: #826b3d !important;
  background-color: #f8f6f1 !important;
}
</style>
